import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { useReadinessStore, evidenceFor, ReviewGateError, type ViewMode } from '@/stores/readinessStore'
import { allObjectives, domainColor, levelName, sectorBenchmark } from '@/lib/indexes/registry'
import { overviewStats, needsConfirmation, liveLevel, liveScore, needsScoreConfirmation, computeIndexScore, computeScore, scoreBand, scoreToPct } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { scoresOf, sparklineSeries, stateAt } from '@/lib/readiness/series'
import type { LevelCode, Snapshot } from '@/api/types/readiness'
import { LEVELS } from '@/api/types/readiness'
import { RdPage, HeaderActions, ReminderBanner } from '@/components/readiness/shell'
import { PreliminaryNotice, PreliminaryChip } from '@/components/readiness/PreliminaryNotice'
import { ScoreRing, DistributionBar, DomainPips, DomainDot, Icon, SectionLabel, Chip } from '@/components/readiness/primitives'
import { ObjectiveCard } from '@/components/readiness/ObjectiveCard'
import { Heatmap, type HeatmapCellData } from '@/components/readiness/Heatmap'
import { useAssessmentHeatmap, useAssessmentObjective, useObjectiveHistory, useObjectiveWrites, csaWordingOf, cellLevel, levelCode, remoteSourceLabel, stateFromCell, undeterminedReason, evidenceFromItem, isEditable, type AssessmentCell } from '@/api/clients/assessmentsApi'
import { isApiError } from '@/api/http'
import { ReviewChangesBanner, ReviewChangesModal } from '@/components/readiness/ReviewChanges'
import { SaveAssessmentModal } from '@/components/readiness/SaveAssessmentModal'
import { useObjectivePlans, useObjectivesPlans } from '@/api/clients/playbookApi'
import type * as playbook from '@/api/types/playbook'
import { formatDate } from '@/components/common/format'
import { QuickViewDrawer } from '@/components/readiness/QuickViewDrawer'
import { ReviewSaveModal } from '@/components/readiness/ReviewSaveModal'
import { Sparkline } from '@/components/readiness/Sparkline'
import { FilterSelect } from '@/components/readiness/FilterSelect'
import { Toolbar, ToolbarSearch, ResultCount, ViewToggle } from '@/components/readiness/Toolbar'
import { Term } from '@/components/readiness/Term'
import { levelToken } from '@/lib/readiness/score'
import { TechDependenciesCard } from '@/components/catalogue/shared'
import { OriginProvider, useFeatureOrigin, useRestoreOnReturn } from '@/lib/readiness/nav'
import { BandWord, ReadinessScoreTooltip } from '@/components/readiness/ScoreTooltip'
import { useReadinessModule } from '@/lib/readiness/module'
import { stepState, monthEnd, plusDays } from '@/lib/catalogue/engine'
import { transitionFor } from '@/api/types/readiness'
import { useIsHidden, useIsLocked } from '@/stores/profileStore'
import { SIGNAL_RULES, QPI_SIGNAL_RULES } from '@/lib/readiness/signals'

type Filter = 'needs_confirmation' | 'no_regrets' | 'manual_pending' | 'below_sector'
type SourceFilter = 'all' | 'discovery' | 'playbook' | 'manual' | 'suggested'
// The Level filter: a level, or Not set (an objective with no level is never L0).
type LevelFilter = LevelCode | 'unset'
// Dashboard view state carried through origin-aware navigation (I5 §4.2).
interface DashboardView { domain: string | null; filters: Filter[]; search: string; levels: LevelFilter[]; source: SourceFilter }

// A served cell's Playbook plans as the drawer lists them: how many, and the
// first as its asset and the replacement NIST names.
const planSummary = (p: playbook.ObjectivePlans | null) => (p ? { count: p.plans?.length ?? 0, first: p.plans?.[0] ? `${p.plans[0].asset} → ${p.plans[0].nist_remediation}` : null } : null)

export default function ReadinessOverview() {
  const ctx = useReadiness()
  const m = useReadinessModule(ctx)
  const { index, scope, orgId, indexId, orgName, actor, hasSynced } = ctx
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const saveSnapshot = useReadinessStore((s) => s.saveSnapshot)
  const confirmLevel = useReadinessStore((s) => s.confirmLevel)
  const confirmScore = useReadinessStore((s) => s.confirmScore)
  const addEvidence = useReadinessStore((s) => s.addEvidence)
  const track = useReadinessStore((s) => s.track)
  const viewMode = useReadinessStore((s) => s.viewMode)
  const setViewMode = useReadinessStore((s) => s.setViewMode)
  const scale = assertScale(index)
  // POC trims (I6 POC §6) and the editable drawer that replaces the objective page (§7).
  const scoreTileHidden = useIsHidden('readiness_score_tile')
  const tileLinksHidden = useIsHidden('dashboard_tile_links')
  const viewToggleHidden = useIsHidden('view_toggle')
  const saveLocked = useIsLocked('save_assessment')
  const detailLocked = useIsLocked('objective_detail')
  // With the toggle removed the grid ("heatmap") view is fixed.
  const view: ViewMode = viewToggleHidden ? 'heatmap' : viewMode
  const location = useLocation()
  // Coming back from a detail page restores filters, scroll and focus (I5 §4.2).
  const restored = useRestoreOnReturn<DashboardView>()
  const origin = useFeatureOrigin('dashboard')

  const [domain, setDomain] = useState<string | null>(restored?.domain ?? null)
  const filtersFromUrl = (params.get('filter') ?? '').split(',').filter(Boolean) as Filter[]
  const [filters, setFilters] = useState<Set<Filter>>(new Set(restored?.filters ?? filtersFromUrl))
  const [search, setSearch] = useState(restored?.search ?? '')
  const [levels, setLevels] = useState<Set<LevelFilter>>(new Set(restored?.levels ?? []))
  const [source, setSource] = useState<SourceFilter>(restored?.source ?? 'all')
  const viewRef = useRef<DashboardView>({ domain, filters: Array.from(filters), search, levels: Array.from(levels), source })
  viewRef.current = { domain, filters: Array.from(filters), search, levels: Array.from(levels), source }
  const getViewState = useCallback(() => viewRef.current, [])
  const [showSave, setShowSave] = useState(params.get('review') === '1' && !saveLocked)
  // A POC deep link to /readiness/{code} lands here with the drawer open (§7.4).
  const [selected, setSelected] = useState<string | null>(() => (location.state as { openObjective?: string } | null)?.openObjective ?? null)
  const [gateError, setGateError] = useState<string | null>(null)

  const remote = useAssessmentHeatmap(orgId, indexId)
  // The served assessment, once loaded, is what every widget on the page reads;
  // the in-browser engine stands in until (or unless) the service answers.
  const states = remote.states ?? scope.states
  const items = useMemo(() => allObjectives(index), [index])
  const stats = useMemo(() => overviewStats(index, states), [index, states])
  const indexScore = useMemo(() => (scale === 'score_0_10' ? computeIndexScore(index, (c) => liveScore(states[c])) : null), [index, states, scale])
  // A served assessment names the one it follows, and each cell carries its
  // level there (previous_level): that is the comparison point. The browser's
  // own snapshots stand in when the service has none.
  const servedPrevious = useMemo<Snapshot | null>(() => {
    const p = remote.previous
    if (!p || !remote.cells) return null
    const items = Array.from(remote.cells.values()).map((c) => ({
      snapshot_id: p.id,
      objective_code: c.objective_code,
      level: `L${c.previous_level ?? 0}` as LevelCode,
      status: 'declared' as const,
      evidence_ids: [],
    }))
    return {
      id: p.id, org_id: orgId, index_id: indexId, index_version: index.version, name: p.title, note: null,
      score: computeScore(items.map((i) => i.level)), created_by: 'system', created_at: p.submitted_at ?? p.created_at,
      as_of: p.as_of_date, baseline: false, scheduled: false, items, discovery_summary: null,
    }
  }, [remote.previous, remote.cells, orgId, indexId, index.version])
  const lastAssessment = servedPrevious ?? scope.snapshots[scope.snapshots.length - 1] ?? null
  const liveNumber = scale === 'levels' ? stats.score : (indexScore?.overall ?? 0)
  const band = scale === 'levels' ? stats.band : scoreBand(scoreToPct(indexScore?.overall ?? null))
  const delta = lastAssessment ? Math.round((liveNumber - lastAssessment.score) * 10) / 10 : null
  const liveLevels = useMemo(() => items.map(({ objective }) => liveLevel(states[objective.code])), [items, states])

  // Sparkline (I2 §3.1): last six assessment points, else the event log bucketed monthly.
  const spark = useMemo(() => {
    const live = scoresOf(index, stateAt(index, scope.events ?? [], new Date().toISOString()))
    return sparklineSeries(index, scope.events ?? [], scope.snapshots, { score: liveNumber, has_suggested: live.has_suggested })
  }, [index, scope.events, scope.snapshots, liveNumber])

  const toggleFilter = (f: Filter) => {
    const next = new Set(filters)
    if (next.has(f)) next.delete(f)
    else next.add(f)
    setFilters(next)
    const p = new URLSearchParams(params)
    if (next.size) p.set('filter', Array.from(next).join(','))
    else p.delete('filter')
    setParams(p, { replace: true })
  }
  const switchView = (mode: ViewMode) => {
    if (mode === view) return
    setViewMode(mode)
    track(orgId, indexId, { name: 'readiness.view_toggled', view: mode })
    if (mode === 'cards') setSelected(null)
  }

  const evidenceCount = (code: string) => remote.cells?.get(code)?.evidence_count ?? evidenceFor(scope, code).length
  // A served cell's plans are Playbook's remediations for it (read for the
  // cards view only); other cells count the in-browser links in progress.
  const cardPlans = useObjectivesPlans(orgId, view === 'cards' && remote.cells ? Array.from(remote.cells.keys()) : [])
  const plansFor = (code: string) => (remote.cells?.has(code) ? (cardPlans.get(code)?.plans?.length ?? 0) : scope.links.filter((l) => l.objective_code === code && l.status === 'in_progress').length)
  const sourcesOf = (code: string): Set<SourceFilter> => {
    const rc = remote.cells?.get(code)
    if (rc) return remoteFacts(rc).sources
    const s = new Set<SourceFilter>()
    evidenceFor(scope, code).forEach((e) => s.add(e.source))
    const st = states[code]
    if (st?.status === 'suggested' || (scale === 'levels' ? needsConfirmation(st) : needsScoreConfirmation(st))) s.add('suggested')
    return s
  }
  const primarySource = (code: string): string | null => {
    const rc = remote.cells?.get(code)
    if (rc) return remoteSourceLabel(rc)
    const ev = evidenceFor(scope, code)
    if (!ev.length) return null
    const counts = { discovery: 0, playbook: 0, manual: 0 }
    ev.forEach((e) => (counts[e.source] += 1))
    const top = (Object.keys(counts) as Array<keyof typeof counts>).sort((a, b) => counts[b] - counts[a])[0]
    return top === 'discovery' ? 'Discovery' : top === 'playbook' ? 'Playbook' : 'Manual'
  }
  const pendingOf = (code: string) => (scale === 'levels' ? needsConfirmation(states[code]) : needsScoreConfirmation(states[code]))
  const belowSector = (code: string) => {
    const s = liveScore(states[code])
    const b = sectorBenchmark(index, code)
    return s != null && b != null && s < b
  }

  // What the state filters test: the in-browser state, or for a heatmap cell
  // served by the readiness service, that cell (so dimming matches what it shows).
  type Facts = { pending: boolean; unset: boolean; level: LevelFilter; sources: Set<SourceFilter> }
  const engineFacts = (code: string): Facts => {
    const st = states[code]
    const unset = !st || st.status === 'unset'
    return { pending: pendingOf(code), unset, level: unset ? 'unset' : liveLevel(st), sources: sourcesOf(code) }
  }
  const remoteFacts = (c: AssessmentCell): Facts => {
    // Readiness's own records are entered by hand, so they filter as Manual.
    const sources = new Set<SourceFilter>((c.evidence_sources ?? []).map((s) => (s === 'readiness' ? 'manual' : s)).filter((s): s is 'discovery' | 'playbook' | 'manual' => s === 'discovery' || s === 'playbook' || s === 'manual'))
    if (c.needs_confirmation) sources.add('suggested')
    return { pending: c.needs_confirmation, unset: c.level == null, level: c.level == null ? 'unset' : cellLevel(c), sources }
  }

  const matches = (code: string, objective: (typeof items)[number]['objective'], d: (typeof items)[number]['domain'], f: Facts = engineFacts(code)) => {
    if (domain && d.code !== domain) return false
    if (filters.size) {
      const ok =
        (filters.has('needs_confirmation') && f.pending) ||
        (filters.has('no_regrets') && objective.no_regrets) ||
        (filters.has('manual_pending') && f.unset) ||
        (filters.has('below_sector') && belowSector(code))
      if (!ok) return false
    }
    if (levels.size && !levels.has(f.level)) return false
    if (source !== 'all' && !f.sources.has(source)) return false
    if (search) {
      const q = search.toLowerCase()
      if (!code.toLowerCase().includes(q) && !objective.title.toLowerCase().includes(q)) return false
    }
    return true
  }
  const visible = items.filter(({ domain: d, objective }) => matches(objective.code, objective, d))

  const counts = {
    needs: items.filter(({ objective }) => pendingOf(objective.code)).length,
    noRegrets: items.filter(({ objective }) => objective.no_regrets).length,
    pending: stats.unset,
    below: items.filter(({ objective }) => belowSector(objective.code)).length,
  }
  const domainsSorted = [...index.domains].sort((a, b) => a.order - b.order)
  const dash = !hasSynced && Object.keys(states).length === 0
  const facts = scope.settings.discovery_facts
  const pfacts = scope.settings.playbook_facts
  // The figure beside a card's title: what Discovery (M1) and Playbook (M3) found in the objective's scope.
  const contextFigure = (code: string): string | null => {
    const parts: string[] = []
    if (facts?.connected) {
      if ((code === 'RA-1' || code === 'PL-4') && facts.asset_count) parts.push(`for the ${facts.asset_count.toLocaleString()}-asset CBOM`)
      if ((code === 'EE-1' || code === 'PP-3') && facts.vendor_components.length) parts.push(`${facts.vendor_components.length} vendor ${facts.vendor_components.length === 1 ? 'component' : 'components'}`)
    }
    if (pfacts?.service_connected) {
      if ((code === 'EE-1' || code === 'PP-3') && pfacts.issuing_cas.length) parts.push(`${pfacts.issuing_cas.length} issuing ${pfacts.issuing_cas.length === 1 ? 'CA' : 'CAs'}`)
      const options = code === 'TE-1' || code === 'PL-3' ? pfacts.remediations.filter((r) => r.vulnerable_count > 0).length : 0
      if (options) parts.push(`${options} replacement ${options === 1 ? 'option' : 'options'} identified`)
    }
    // Counts of things found read "… found"; "for the …" and "… identified" already say it.
    return parts.length ? `(${parts.join(', ')}${parts.some((p) => p.startsWith('for the') || p.endsWith('identified')) ? '' : ' found'})` : null
  }

  // Heatmap + drawer state (I2 §4, §5, Note 3): reading order is the grid's.
  const order = items.map(({ objective }) => objective.code)
  const cells: HeatmapCellData[] = items.map(({ domain: d, objective }) => {
    const rc = remote.cells?.get(objective.code)
    return { domain: d, objective, state: states[objective.code], evidenceCount: evidenceCount(objective.code), primarySource: primarySource(objective.code), visible: matches(objective.code, objective, d, rc ? remoteFacts(rc) : undefined), remote: rc }
  })
  // Where the heatmap's cells come from; the in-browser engine stands in until (or unless) the service answers.
  const heatmapCaption =
    remote.status === 'ready' && remote.heatmap
      ? `${remote.heatmap.assessment.title} · ${remote.heatmap.assessment.status.replace(/_/g, ' ')} · as of ${formatDate(remote.heatmap.assessment.as_of_date)} · from the readiness service`
      : remote.status === 'loading'
        ? 'Loading the assessment from the readiness service…'
        : remote.status === 'empty'
          ? 'The readiness service has no assessment for this organisation yet; showing in-browser data.'
          : remote.status === 'error'
            ? `Couldn't load the assessment from the readiness service (${remote.error instanceof Error ? remote.error.message : String(remote.error)}); showing in-browser data.`
            : undefined
  const selectCell = useCallback(
    (code: string) => {
      setSelected(code)
      track(orgId, indexId, { name: 'readiness.quickview_opened', objective_code: code })
    },
    [orgId, indexId, track]
  )
  // A link that names an objective opens its drawer, also when the page is
  // already showing (the sync summary links here); then the state is dropped
  // so a reload starts closed.
  const openObjective = (location.state as { openObjective?: string } | null)?.openObjective
  useEffect(() => {
    if (!openObjective) return
    setSelected(openObjective)
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openObjective])
  const selectedItem = selected ? items.find(({ objective }) => objective.code === selected) ?? null : null
  // A served cell's drawer reads the objective detail: who set the level, the note, the evidence.
  const selectedCell = selected ? remote.cells?.get(selected) ?? null : null
  const servedId = selectedCell ? remote.heatmap!.assessment.id : null
  const detail = useAssessmentObjective(orgId, servedId, selectedCell ? selected : null).data ?? null
  const history = useObjectiveHistory(orgId, servedId, selectedCell ? selected : null)
  const writes = useObjectiveWrites(orgId, servedId, selectedCell ? selected : null)
  const servedEditable = !!remote.heatmap && isEditable(remote.heatmap.assessment.status)
  const writeError = (e: unknown, action = 'change levels') =>
    e == null ? null
    : isApiError(e, 'LOGIN_REQUIRED') ? `Sign in as a person to ${action}.`
    : isApiError(e, 'ASSESSOR_REQUIRED') ? `Only an assessor or an administrator can ${action}. Ask one of them, or ask for the role.`
    : e instanceof Error ? e.message : String(e)
  const [reviewOpen, setReviewOpen] = useState(false)
  // A served draft saves to the service; `?save=1` (the report's link) opens it.
  const [servedSave, setServedSave] = useState(params.get('save') === '1')
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const reviewCells = useMemo(() => (remote.cells ? Array.from(remote.cells.values()).filter((c) => c.needs_confirmation) : []), [remote.cells])
  const selectedPlans = useObjectivePlans(orgId, selectedCell ? selected : null).data ?? null
  const scrollRef = useRef<number>(0)
  useEffect(() => {
    // Toggling the view keeps the scroll position (I2 §3.2).
    window.scrollTo({ top: scrollRef.current })
  }, [view])

  const onSave = (input: { name: string; note: string | null; as_of: string }) => {
    try {
      const snap = saveSnapshot(orgId, indexId, { ...input, projection: m.projection ? { summary: m.projection.summary, gates: m.gates } : null }, actor)
      setShowSave(false)
      setGateError(null)
      navigate(`/readiness/progress?index=${indexId}&to=${snap.id}`)
    } catch (e) {
      setGateError(e instanceof ReviewGateError ? `Blocking checks failed: ${e.codes.join(', ')}` : String(e))
    }
  }
  const startingSet = index.starting_set
  const stepDone = (item: NonNullable<typeof startingSet>['items'][number]) => {
    const codes = item.objective_codes
    if (codes.some((c) => (liveScore(states[c]) ?? 0) > 0)) return true
    if (item.signal === 'discovery.inventory_exists') return !!facts?.connected && facts.inventories.length > 0
    if (item.signal === 'playbook.plan_any') return scope.playbook_actions.length > 0
    if (item.signal === 'playbook.vendor_action') return scope.playbook_actions.some((a) => /vendor/i.test(a.title) && a.status !== 'open')
    return false
  }

  return (
    <OriginProvider origin={origin} getViewState={getViewState}>
    <RdPage
      title="Dashboard"
      subtitle={`${index.name} ${index.version} · where ${orgName || 'the organisation'} stands today across ${index.domains.length} ${scale === 'levels' ? 'domains' : 'dimensions'}, ${stats.objectives} ${scale === 'levels' ? 'objectives' : 'sub-indices'}`}
      right={<HeaderActions ctx={ctx} saveServed={servedEditable} onSaveSnapshot={() => (servedEditable ? setServedSave(true) : setShowSave(true))} onExport={() => track(orgId, indexId, { name: 'readiness.summary_report_exported' })} />}
      testId="readiness-overview"
    >
      <PreliminaryNotice index={index} />
      <ReminderBanner ctx={ctx} onReview={() => setShowSave(true)} />
      <ReviewChangesBanner cells={reviewCells} onReview={() => setReviewOpen(true)} />
      {savedNote && (
        <div className="mb-4 flex items-center gap-2 rounded-[10px] px-4 py-2.5 text-[12.5px]" style={{ background: 'var(--rd-status-ok-bg)', color: 'var(--rd-status-ok-text)' }} role="status" data-testid="saved-banner">
          <Icon name="check" size={14} /> {savedNote}
          <button type="button" className="rd-link ml-auto" onClick={() => setSavedNote(null)}>Dismiss</button>
        </div>
      )}

      {/* Summary row (§6.2): identical in both views (I2 §3.1). Iteration 3 §2: the
          fourth tile is Technology dependencies; under 1100px it drops to a full-width strip. */}
      {/* POC (I6 POC §6.1): the Readiness score tile is removed and the strip is three tiles. */}
      <div className={`mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3 ${scoreTileHidden ? '' : 'min-[1100px]:grid-cols-4'}`} data-testid="dashboard-kpis">
        {!scoreTileHidden && (
        <section className="rd-card p-5" aria-labelledby="rd-score-label">
          <div className="flex items-center justify-between gap-2">
            <SectionLabel><span id="rd-score-label"><Term index={index} term="Readiness score" className="!no-underline uppercase">Readiness score</Term></span></SectionLabel>
            {index.aggregation === 'native' ? <PreliminaryChip index={index} label="Indicative" /> : (
              /* The chip opens the shared score tooltip (I5 §6.4). */
              <ReadinessScoreTooltip index={index} levels={liveLevels} orgName={orgName} score={stats.score} surface="dashboard" orgId={orgId}>
                <span className="rd-chip" style={{ background: 'var(--rd-accent-chip-bg)', color: 'var(--rd-accent-chip-fg)' }} data-testid="score-indicator-chip">Quantizant indicator</span>
              </ReadinessScoreTooltip>
            )}
          </div>
          {/* Ring left, text column right (I4 §7.2) */}
          <div className="mt-3 flex items-center gap-[18px]">
            <ScoreRing size={120} stroke={12} score={dash ? 0 : scale === 'levels' ? stats.score : scoreToPct(indexScore?.overall ?? null)} color={band.token}>
              <span className="relative text-[30px] font-bold leading-none" data-testid="readiness-score">
                {dash ? '—' : scale === 'levels' ? stats.score : fmtScore(indexScore?.overall ?? null)}
                {!dash && counts.needs > 0 && (
                  <Term index={index} term="Suggested level" className="!no-underline">
                    <span className="absolute -right-3 top-0 inline-block h-2 w-2 rounded-full" style={{ background: 'var(--rd-status-warn)' }} title={`${counts.needs} suggested ${scale === 'levels' ? 'levels' : 'scores'} not yet confirmed`} aria-label={`${counts.needs} suggested ${scale === 'levels' ? 'levels' : 'scores'} not yet confirmed`} />
                  </Term>
                )}
              </span>
            </ScoreRing>
            <div className="min-w-0">
              <p className="text-[19px] font-bold" style={{ color: band.token }}>{dash ? '—' : scale === 'levels' ? <BandWord band={band} testId="dashboard-band" /> : `${fmtScore(indexScore?.overall ?? null)} of 10`}</p>
              <p className="text-[12.5px] leading-snug" style={{ color: 'var(--rd-text-muted)' }}>{scale === 'levels' ? `Mean level across ${stats.objectives} objectives, as % of L3` : `Mean of ${index.domains.length} dimension scores${indexScore?.unscored ? ` · ${indexScore.unscored} sub-${indexScore.unscored === 1 ? 'index' : 'indices'} unscored, excluded` : ''}`}</p>
              {!dash && spark.length >= 2 && <div className="mt-2"><Sparkline points={spark} color={band.token} max={scale === 'levels' ? 100 : 10} /></div>}
              <div className="mt-1.5 text-[12.5px] leading-snug" style={{ color: 'var(--rd-text-secondary)' }}>
                <span data-testid="score-delta">
                  {dash ? '—' : delta != null ? `${delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : '='}${delta === 0 ? '' : delta} since ${lastAssessment!.name}` : 'No earlier assessment'}
                  {scale === 'levels' ? <> · {stats.noRegretsStarted} of {stats.noRegretsTotal} <Term index={index} term="No-regrets">no-regrets</Term> started</> : index.benchmarks ? <> · sector {fmtScore(index.benchmarks.overall)}</> : null}
                </span>
              </div>
            </div>
          </div>
          <p className="mt-3 text-[11.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)' }}>
            {index.aggregation === 'native' ? (
              <>The overall figure is the {index.name}'s own construct. Sector benchmarks: {index.benchmarks?.by_label ? Object.entries(index.benchmarks.by_label).map(([k, v]) => `${k} ${v.toFixed(1)}`).join(' · ') : '—'} ({index.benchmarks?.source}).</>
            ) : (
              <>The <Term index={index} term="QRI">QRI</Term> itself does not aggregate. This score is Quantizant's summary for leadership; the levels below are the CSA measure.</>
            )}
          </p>
        </section>
        )}

        <section className="rd-card p-5" aria-labelledby="rd-dist-label">
          {scale === 'levels' ? (
            <>
              <SectionLabel><span id="rd-dist-label">Objectives by <Term index={index} term="Level" className="!no-underline uppercase">level</Term></span></SectionLabel>
              <div className="mt-3">
                <DistributionBar distribution={stats.distribution} unset={stats.unset} total={dash ? 0 : stats.objectives} />
              </div>
              {/* Five columns when the card is wide; they wrap to a second row rather than clip a level name. */}
              <div className="mt-3 grid gap-x-2 gap-y-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(64px, 1fr))' }}>
                {LEVELS.map((l) => (
                  <div key={l} className="min-w-0">
                    <p className="text-[24px] font-bold leading-none" data-testid={`dist-${l}`}>{dash ? '—' : stats.distribution[l]}</p>
                    <p className="rd-mono mt-1.5 whitespace-nowrap text-[12px] font-semibold" style={{ color: levelToken(l) }}>{l}</p>
                    <p className="text-[12px] leading-tight" style={{ color: 'var(--rd-text-muted)' }}>{levelName(index, l)}</p>
                  </div>
                ))}
                <div className="min-w-0">
                  <p className="text-[24px] font-bold leading-none" data-testid="dist-unset">{dash ? '—' : stats.unset}</p>
                  <p className="rd-mono mt-1.5 whitespace-nowrap text-[12px] font-semibold" style={{ color: 'var(--rd-text-muted)' }}>○</p>
                  <p className="text-[12px] leading-tight" style={{ color: 'var(--rd-text-muted)' }}>Not set</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <SectionLabel><span id="rd-dist-label">Dimensions</span></SectionLabel>
              <ul className="mt-3 space-y-2">
                {indexScore?.byDomain.map((d) => {
                  const def = domainsSorted.find((x) => x.code === d.code)!
                  const color = domainColor(def, theme)
                  const bench = def.benchmark?.sector ?? null
                  return (
                    <li key={d.code} className="flex items-center gap-2 text-[12.5px]">
                      <DomainDot domain={def} theme={theme} size={10} />
                      <span className="min-w-0 flex-1 truncate">{d.name}</span>
                      <span className="relative h-2 w-24 rounded-full" style={{ background: 'var(--rd-bg-tile)' }} aria-hidden="true">
                        <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${((d.score ?? 0) / 10) * 100}%`, background: color }} />
                        {bench != null && <span className="absolute -top-0.5 h-3 w-0.5" style={{ left: `${(bench / 10) * 100}%`, background: 'var(--rd-text-secondary)' }} />}
                      </span>
                      <span className="rd-mono w-[72px] text-right" style={{ color: 'var(--rd-text-secondary)' }} data-testid={`dim-${d.code}`}>{fmtScore(d.score)}{bench != null ? <span style={{ color: 'var(--rd-text-faint)' }}> / {bench.toFixed(1)}</span> : null}</span>
                    </li>
                  )
                })}
              </ul>
              <p className="mt-2 text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>Your score / sector mean</p>
            </>
          )}
          <p className="mt-4 flex items-center justify-between text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
            <span>
              {counts.needs} {counts.needs === 1 ? (scale === 'levels' ? 'level needs' : 'score needs') : (scale === 'levels' ? 'levels need' : 'scores need')} your confirmation
            </span>
            {/* Review → is hidden for now: levels are confirmed in the assessment, not in this browser. */}
          </p>
        </section>

        {scale === 'levels' ? (
          <section className="rd-card p-5" aria-labelledby="rd-dom-label">
            <div className="flex items-center justify-between gap-2">
              <SectionLabel><span id="rd-dom-label">By domain</span></SectionLabel>
              {/* Each domain has two objectives: the text reads objective 1, then objective 2. */}
              <span className="rd-mono flex text-[11px]" style={{ color: 'var(--rd-text-faint)' }} data-testid="by-domain-header">
                <span className="w-[48px] text-center">Obj 1</span>
                <span className="w-[48px] text-center">Obj 2</span>
              </span>
            </div>
            <ul className="mt-3 space-y-2">
              {stats.byDomain.map((d) => {
                const def = domainsSorted.find((x) => x.code === d.code)!
                const color = domainColor(def, theme)
                return (
                  <li key={d.code} className="flex items-center gap-2 text-[14px]">
                    <DomainDot domain={def} theme={theme} size={10} />
                    <span className="min-w-0 flex-1 truncate">{d.name}</span>
                    <span title="Both objectives combined, out of L3 + L3"><DomainPips levels={dash ? [] : d.levels} color={color} /></span>
                    {/* Each level sits centred under its own Obj column. */}
                    <span className="rd-mono flex text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }} title={dash ? undefined : `Objective 1: ${d.labels[0]} · Objective 2: ${d.labels[1] ?? '—'}`} data-testid={`by-domain-${d.code}`}>
                      {[0, 1].map((i) => (
                        <span key={i} className="w-[48px] text-center">{dash || !d.labels[i] || d.labels[i] === 'Not set' ? '—' : d.labels[i]}</span>
                      ))}
                    </span>
                  </li>
                )
              })}
            </ul>
            <p className="mt-3 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Bars: both objectives combined, out of L3 + L3 · — not set</p>
            <p className="mt-1 flex items-center justify-between text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
              <span className="truncate">Weakest: {dash ? '—' : stats.weakest.join(', ')}</span>
              {!tileLinksHidden && <Link to={`/readiness/progress?index=${indexId}`} className="rd-link shrink-0">Progress →</Link>}
            </p>
          </section>
        ) : (
          startingSet?.render === 'checklist' && (
            <section className="rd-card p-5" aria-labelledby="rd-steps-label" data-testid="starting-set">
              <SectionLabel><span id="rd-steps-label">{startingSet.label}</span></SectionLabel>
              <ul className="mt-3 space-y-2">
                {startingSet.items.map((it) => {
                  const done = stepDone(it)
                  return (
                    <li key={it.id} className="flex items-start gap-2 text-[12.5px]">
                      <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full" style={{ border: `2px solid ${done ? 'var(--rd-status-ok)' : 'var(--rd-border-control)'}`, color: 'var(--rd-status-ok-text)' }} aria-label={done ? 'done' : 'open'}>
                        {done && <Icon name="check" size={10} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        {it.text} <span style={{ color: 'var(--rd-text-faint)' }}>· {it.objective_codes.join(', ')}{it.source_page ? ` · ${it.source_page}` : ''}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
              <p className="mt-4 flex items-center justify-between text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                <span>{startingSet.items.filter(stepDone).length} of {startingSet.items.length} started</span>
                {!tileLinksHidden && <Link to={`/readiness/progress?index=${indexId}`} className="rd-link shrink-0">Progress →</Link>}
              </p>
            </section>
          )
        )}
        <TechDependenciesCard m={m} className={scoreTileHidden ? '' : 'lg:col-span-3 min-[1100px]:col-span-1'} hideLink={tileLinksHidden} />
      </div>

      {dash && (
        <section className="rd-card mb-4 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="first-sync-cta">
          <div>
            <p className="rd-section-title">Run your first sync to let Quantizant suggest readiness {scale === 'levels' ? 'levels' : 'scores'} from Discovery and Playbook</p>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Manual-only {scale === 'levels' ? 'objectives' : 'sub-indices'} stay yours to declare: open a card and add evidence.</p>
          </div>
          <button type="button" className="rd-btn rd-btn-primary" onClick={() => void ctx.sync('manual')} disabled={ctx.syncing}>
            <Icon name="sync" size={14} className={ctx.syncing ? 'animate-spin' : ''} /> Sync now
          </button>
        </section>
      )}

      {/* Board (§6.3) */}
      <div className="flex flex-wrap gap-5">
        <aside className="rd-card w-full p-2.5 lg:w-[248px] lg:shrink-0" aria-label="Domains and filters">
          <button type="button" onClick={() => setDomain(null)} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-[14px] font-medium" style={domain === null ? { background: 'var(--rd-accent-primary)', color: '#fff' } : undefined} aria-pressed={domain === null}>
            <span>All {scale === 'levels' ? 'objectives' : 'sub-indices'}</span>
            <span className="rd-mono text-[12.5px]">{items.length}</span>
          </button>
          {domainsSorted.map((d) => (
            <button key={d.code} type="button" onClick={() => setDomain(d.code)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[14px]" style={domain === d.code ? { background: 'var(--rd-bg-tile)' } : undefined} aria-pressed={domain === d.code}>
              <DomainDot domain={d} theme={theme} />
              <span className="min-w-0 flex-1 truncate text-left">{d.name}</span>
              <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{d.objectives.length}</span>
            </button>
          ))}
          <div className="my-2" style={{ borderTop: '1px solid var(--rd-border-subtle)' }} />
          <p className="rd-label px-3 py-1">Filter</p>
          {(
            scale === 'levels'
              ? [
                  { key: 'needs_confirmation', label: 'Needs confirmation', dot: 'var(--rd-status-warn)', count: counts.needs, countColor: 'var(--rd-status-warn)', term: 'Suggested level' },
                  { key: 'no_regrets', label: 'No-regrets only', dot: 'var(--rd-accent-chip-fg)', count: counts.noRegrets, term: 'No-regrets' },
                  { key: 'manual_pending', label: 'Manual input pending', dot: 'var(--rd-text-muted)', count: counts.pending },
                ]
              : [
                  { key: 'below_sector', label: 'Below sector average', dot: 'var(--rd-status-info)', count: counts.below },
                  { key: 'needs_confirmation', label: 'Needs confirmation', dot: 'var(--rd-status-warn)', count: counts.needs, countColor: 'var(--rd-status-warn)', term: 'Suggested level' },
                  { key: 'manual_pending', label: 'Not yet scored', dot: 'var(--rd-text-muted)', count: counts.pending },
                ]
          ).map((f) => (
            <button key={f.key} type="button" onClick={() => toggleFilter(f.key as Filter)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[14px]" style={filters.has(f.key as Filter) ? { background: 'var(--rd-bg-tile)' } : undefined} aria-pressed={filters.has(f.key as Filter)} data-testid={`filter-${f.key}`}>
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: f.dot }} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-left">{'term' in f && f.term ? <Term index={index} term={f.term} className="!no-underline" as="span">{f.label}</Term> : f.label}</span>
              <span className="rd-mono text-[12.5px]" style={{ color: ('countColor' in f && f.countColor) || 'var(--rd-text-muted)' }}>{f.count}</span>
            </button>
          ))}
        </aside>

        <div className="min-w-0" style={{ flex: '999 1 560px' }}>
          <Toolbar className="mb-3" testId="overview-toolbar">
            <ToolbarSearch label="Search objectives" placeholder={`Search ${scale === 'levels' ? 'objectives' : 'sub-indices'}`} value={search} onChange={setSearch} />
            {scale === 'levels' && (
              <FilterSelect multiple label="Level" value={Array.from(levels)} onChange={(v) => setLevels(new Set(v as LevelFilter[]))} options={[...LEVELS.map((l): [string, string] => [l, `${l} ${levelName(index, l)}`]), ['unset', 'Not set']]} testId="filter-level" />
            )}
            <FilterSelect label="Source" value={source} onChange={(v) => setSource(v as SourceFilter)} defaultValue="all" options={[['all', 'All'], ['discovery', 'Discovery'], ['playbook', 'Playbook'], ['manual', 'Manual'], ['suggested', 'Suggested']]} testId="filter-source" />
            <ResultCount testId="objective-count">Sorted by {scale === 'levels' ? 'domain' : 'dimension'} · {visible.length} {scale === 'levels' ? 'objectives' : 'sub-indices'}</ResultCount>
            {/* View toggle (I2 §3.1): list = cards, grid = heatmap. Removed in POC (I6 POC §6.3). */}
            {!viewToggleHidden && (
              <ViewToggle<ViewMode>
                label="View"
                value={view}
                onChange={(mode) => { scrollRef.current = window.scrollY; switchView(mode) }}
                options={[{ value: 'cards', icon: 'list', label: 'Card view', testId: 'view-cards' }, { value: 'heatmap', icon: 'grid', label: 'Heatmap view', testId: 'view-heatmap' }]}
                testId="view-toggle"
              />
            )}
          </Toolbar>

          {view === 'heatmap' ? (
            <Heatmap index={index} cells={cells} theme={theme} lastAssessment={lastAssessment} selected={selected} onSelect={selectCell} caption={heatmapCaption} />
          ) : (
            <>
              {domainsSorted.map((d) => {
                const inDomain = visible.filter((v) => v.domain.code === d.code)
                if (!inDomain.length) return null
                const color = domainColor(d, theme)
                return (
                  <section key={d.code} id={`domain-${d.code}`} className="mb-5" aria-labelledby={`domain-h-${d.code}`}>
                    <h2 id={`domain-h-${d.code}`} className="mb-2 flex flex-wrap items-center gap-2 text-[12.5px] font-semibold uppercase tracking-wider" style={{ color }}>
                      {d.name} <span className="normal-case tracking-normal" style={{ color: 'var(--rd-text-faint)', fontWeight: 400 }}>· {d.summary}</span>
                      {scale === 'score_0_10' && d.benchmark && <Chip tone="neutral" className="normal-case tracking-normal">sector {d.benchmark.sector.toFixed(1)}</Chip>}
                    </h2>
                    <div className="space-y-3">
                      {inDomain.map(({ objective }) => (
                        <ObjectiveCard key={objective.code} index={index} domain={d} objective={objective} state={states[objective.code]} evidenceCount={evidenceCount(objective.code)} evidenceSources={Array.from(sourcesOf(objective.code))} plansInProgress={plansFor(objective.code)} color={color} contextFigure={contextFigure(objective.code)} />
                      ))}
                    </div>
                  </section>
                )
              })}
              {visible.length === 0 && (
                <div className="rd-card p-8 text-center text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>
                  No {scale === 'levels' ? 'objectives' : 'sub-indices'} match the current filters.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {servedSave && servedEditable && remote.heatmap && (
        <SaveAssessmentModal
          orgId={orgId}
          assessmentId={remote.heatmap.assessment.id}
          cells={remote.cells ? Array.from(remote.cells.values()) : []}
          onReview={() => { setServedSave(false); setReviewOpen(true) }}
          onClose={() => setServedSave(false)}
          onSaved={(title) => {
            setServedSave(false)
            setSavedNote(`Saved as "${title}". You're now working in a new draft with the same levels.`)
          }}
        />
      )}

      {reviewOpen && (
        <ReviewChangesModal
          index={index}
          cells={reviewCells}
          onOpen={(code) => {
            setReviewOpen(false)
            if (view !== 'heatmap') switchView('heatmap')
            selectCell(code)
          }}
          onClose={() => setReviewOpen(false)}
        />
      )}

      {view === 'heatmap' && selectedItem && (
        <QuickViewDrawer
          width={detailLocked || selectedCell ? 640 : undefined}
          edit={selectedCell ? (servedEditable ? {
            manualOnly: true,
            requireNote: true,
            busy: writes.setLevel.isPending,
            error: writeError(writes.setLevel.error),
            // The level and the reason go to the assessment, attributed to the signed-in user.
            onSetLevel: (l, note) => {
              writes.setLevel.mutate({ level: Number(l.slice(1)), rationale: note })
              track(orgId, indexId, { name: 'readiness.drawer_action', action: 'set_level', code: selectedItem.objective.code })
            },
            onSetScore: () => undefined,
          } : null) : detailLocked ? {
            manualOnly: scale === 'levels' ? SIGNAL_RULES.filter((r) => r.objective_code === selectedItem.objective.code).every((r) => r.signal_key === 'manual_only') : !QPI_SIGNAL_RULES.some((r) => r.objective_code === selectedItem.objective.code),
            // Same store actions, events and audit entries as the objective page (§7.3).
            onSetLevel: (l, note, cause) => {
              confirmLevel(orgId, indexId, selectedItem.objective.code, l, note, actor, cause)
              track(orgId, indexId, { name: 'readiness.drawer_action', action: 'set_level', code: selectedItem.objective.code })
            },
            onSetScore: (input, note) => {
              confirmScore(orgId, indexId, selectedItem.objective.code, input, note, actor)
              track(orgId, indexId, { name: 'readiness.drawer_action', action: 'set_level', code: selectedItem.objective.code })
            },
            onAddEvidence: (input) => {
              addEvidence(orgId, indexId, { objective_code: selectedItem.objective.code, ...input }, actor)
              track(orgId, indexId, { name: 'readiness.drawer_action', action: 'add_evidence', code: selectedItem.objective.code })
            },
          } : null}
          index={index}
          domain={selectedItem.domain}
          objective={selectedItem.objective}
          state={selectedCell ? stateFromCell(selectedCell, remote.heatmap!.assessment, detail) : states[selectedItem.objective.code]}
          evidence={selectedCell ? (detail?.evidence ?? []).map((e) => evidenceFromItem(e, orgId, selectedItem.objective.code)) : evidenceFor(scope, selectedItem.objective.code)}
          readOnly={selectedCell && !servedEditable ? `${remote.heatmap!.assessment.title} is ${remote.heatmap!.assessment.status.replace(/_/g, ' ')} and read-only. Start a re-assessment to change levels.` : undefined}
          review={selectedCell && servedEditable && selectedCell.needs_confirmation && selectedCell.review_reason ? {
            reason: selectedCell.review_reason,
            current: levelCode(selectedCell.level),
            suggested: levelCode(selectedCell.suggested_level),
            suggestionReason: selectedCell.suggestion_reason,
            source: selectedCell.suggestion_source,
            previousAssessment: remote.previous?.title ?? null,
            busy: writes.review.isPending,
            error: writeError(writes.review.error),
            onAccept: (note) => writes.review.mutate({ decision: 'accept', note }),
            onKeep: (note) => writes.review.mutate({ decision: 'keep', note }),
          } : null}
          undetermined={selectedCell && undeterminedReason(selectedCell) ? { reason: undeterminedReason(selectedCell)!, source: selectedCell.suggestion_source } : null}
          history={selectedCell ? (history.data ?? (history.isError ? [] : null)) : undefined}
          served={!!selectedCell}
          csaWording={selectedCell ? csaWordingOf(detail) : null}
          nextSteps={selectedCell ? (detail?.next_steps ?? []).map((st) => st.text) : null}
          links={selectedCell ? [] : scope.links.filter((l) => l.objective_code === selectedItem.objective.code)}
          plans={selectedCell ? planSummary(selectedPlans) : undefined}
          color={domainColor(selectedItem.domain, theme)}
          theme={theme}
          lastAssessment={lastAssessment}
          prev={order[order.indexOf(selectedItem.objective.code) - 1] ?? null}
          next={order[order.indexOf(selectedItem.objective.code) + 1] ?? null}
          onNavigate={selectCell}
          onConfirm={() => {
            const st = states[selectedItem.objective.code]
            if (!st) return
            // Same effect and audit entry as confirming on the objective page (I2 §5.2).
            if (scale === 'levels' && st.suggested_level) confirmLevel(orgId, indexId, selectedItem.objective.code, st.suggested_level, '', actor)
            else if (scale === 'score_0_10' && st.suggested_score != null) confirmScore(orgId, indexId, selectedItem.objective.code, { tier: st.suggested_tier, score: st.suggested_score }, '', actor)
          }}
          onClose={() => setSelected(null)}
          gated={(() => {
            if (selectedCell) {
              const g = selectedPlans?.vendor_gated
              // Noon UTC keeps the deadline's month in every time zone.
              return g?.count ? { count: g.count, earliest: g.earliest ? `${g.earliest}T12:00:00Z` : null, of: 'dependencies' as const } : null
            }
            if (scale !== 'levels') return null
            const c = selectedItem.objective.code
            const tr = transitionFor(liveLevel(states[c]))
            const n = (selectedItem.objective.recommendations[tr] ?? []).map((_, i) => stepState(c, tr, i, m.mscope, m.catalogue.usage, m.catalogue.technologies, m.catalogue.capabilities)).filter((x) => x.state === 'vendor_gated')
            if (!n.length) return null
            const vd = n.map((x) => x.vendor_date).filter((x): x is string => !!x).sort().pop()
            return { count: n.length, earliest: vd ? plusDays(monthEnd(vd), 30) : null }
          })()}
        />
      )}

      {showSave && (
        <>
          <ReviewSaveModal index={index} scope={scope} actor={actor} onClose={() => { setShowSave(false); setGateError(null) }} onSave={onSave} />
          {gateError && <p className="sr-only" role="alert">{gateError}</p>}
        </>
      )}
    </RdPage>
    </OriginProvider>
  )
}
